import { notifications } from '@mantine/notifications';
import { IconFolders, IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import { useActiveProjectScope, useProductAccess } from '@xver/hooks/useActiveProject';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import { useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import type { ContainerResponse, GetContainerResponse } from '@xver/apis/v1/kufer/containers/model';
import { setActiveProject } from '@xver/store/project/project.actions';
import {
    containerProbeCandidates,
    firstProjectHolding,
    isScopeTerminalError,
} from '@xver/utils/projects';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const CONTAINER_DETAIL_QUERY_KEY = 'apis.v1.kufer.containers.get';

type QueryError = { status?: number; message?: string } | null | undefined;

function isWrapped(value: ContainerResponse | GetContainerResponse): value is GetContainerResponse {
    return (
        value !== null &&
        typeof value === 'object' &&
        'container' in value &&
        (value as GetContainerResponse).container !== undefined
    );
}

/**
 * Where the search of the caller's other projects stands: `missing` once it found nothing while
 * the active project gives no container access, `failed` when a project answered with something
 * other than 403/404 (offer `retryProbe`).
 */
export type ContainerProbeState = 'idle' | 'probing' | 'missing' | 'failed';

/**
 * The container under the active project. On a 404, or when the active project gives no container
 * access, it looks in the caller's other projects and switches to the one holding it. A 404 that
 * no project explains shows "not found" and leaves the page.
 */
export function useContainerQuery(containerId: string | undefined) {
    const { t } = useTranslation('containerEngineInstance');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const { data: projects } = useProjectsQuery();
    const access = useProductAccess('container');
    const [probe, setProbe] = useState<ContainerProbeState>('idle');
    const [attempt, setAttempt] = useState(0);
    const probedKey = useRef<string | null>(null);
    const isProbing = useRef(false);
    const isMounted = useRef(true);

    const query = useQuery<ContainerResponse | undefined>({
        queryKey: [CONTAINER_DETAIL_QUERY_KEY, containerId],
        queryFn: async (): Promise<ContainerResponse | undefined> => {
            if (!containerId) return undefined;
            const response = await Endpoints.v1.Kufer.Containers.getContainer(containerId);
            if (!response) return undefined;
            if (isWrapped(response)) {
                return response.container;
            }
            return response as ContainerResponse;
        },
        retry: (failureCount, err) => !isScopeTerminalError(err) && failureCount < 2,
        refetchInterval: (current) => (isScopeTerminalError(current.state.error) ? false : 5000),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        enabled: !!containerId && isResolved && access !== 'none',
    });

    const errStatus = (query.error as QueryError)?.status;
    const { errorUpdatedAt, refetch } = query;
    const hasNoAccess = access === 'none';
    const probeKey = !containerId
        ? null
        : hasNoAccess
          ? `none:${containerId}:${activeProjectId}:${attempt}`
          : errStatus === 404
            ? `404:${errorUpdatedAt}:${attempt}`
            : null;

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!probeKey || !containerId || isProbing.current) return;
        if (probedKey.current === probeKey) return;
        probedKey.current = probeKey;
        isProbing.current = true;
        setProbe('probing');
        const candidates =
            userId === undefined ? [] : containerProbeCandidates(projects ?? [], activeProjectId);

        void (async () => {
            let found: (typeof candidates)[number] | undefined;
            try {
                found = await firstProjectHolding(
                    candidates,
                    (project) =>
                        Endpoints.v1.Kufer.Containers.getContainer(containerId, project.id),
                    () => !isMounted.current
                );
            } catch {
                isProbing.current = false;
                if (isMounted.current) setProbe('failed');
                return;
            }
            isProbing.current = false;
            if (!isMounted.current) return;
            if (found && userId !== undefined) {
                setProbe('idle');
                dispatch(setActiveProject({ userId, projectId: found.id }));
                notifications.show({
                    title: t('projects:aligned.title'),
                    message: t('projects:aligned.message', { name: found.name }),
                    icon: <IconFolders size={20} aria-hidden="true" />,
                });
                void refetch();
                return;
            }
            if (hasNoAccess) {
                setProbe('missing');
                return;
            }
            setProbe('idle');
            notifications.show({
                title: t('notFound.title', 'Container not found'),
                message: t(
                    'notFound.message',
                    "We couldn't find this container in any of your projects. Redirecting to the container list."
                ),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} aria-hidden="true" />,
            });
            navigate('/container-engine', { replace: true });
        })();
    }, [
        probeKey,
        probe,
        hasNoAccess,
        containerId,
        userId,
        projects,
        activeProjectId,
        dispatch,
        navigate,
        refetch,
        t,
    ]);

    const retryProbe = () => {
        setProbe('idle');
        setAttempt((previous) => previous + 1);
    };

    return { query, probe: probeKey ? probe : 'idle', retryProbe };
}
